/* eslint-disable max-len */

import { useTranslation } from 'react-i18next';
import {
  capitalizeFirstLetterEveryword
} from '../../../shared/stringFunctions';
import { TXT_ADD_WEATHERING, TXT_WEATHERING } from '../../../translations/translationConstants';
import {
  TXT_SEARCH_BY_NAME
} from '../../../translations/translationConstants';
import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import {
  addWeatheringInput,
  deleteWeatheringInput,
  getLithologiesInput,
  getWeatheringOutput,
  GET_WEATHERING,
  updateWeatheringInput
} from '../../../api';
import { useEffect } from 'react';
import { useState } from 'react';
import { ManageWeatheringsTable } from './ManageWeatheringTable';
import {
  DeleteConfirmation,
  ExportToExcel,
  globalPageSizeOptions,
  PaginationBar
} from '../../../components';
import { weathering } from '../../../entityModel';
import { useApolloErrorHandler, useAuthServices } from '../../../hooks';
import {
  ADD_WEATHERING,
  DELETE_WEATHERING,
  UPDATE_WEATHERING
} from '../../../api';
import { ManageWeatheringEditor } from './ManagerWeatheringEditor';

export const ManageWeatherings = () => {
  const { t } = useTranslation();
  const { isSignedIn } = useAuthServices();

  const [txtSearch, setTxtSearch] = useState('');
  const [alterationData, setAlterationData] = useState<weathering[] | undefined>([]);

  const onTxtSearchChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTxtSearch(event.currentTarget.value);
  };

  const [selectedWeathering, setSelectedWeathering] = useState<weathering | undefined>(undefined);
  const [getWeatherings, { loading, data, error, refetch }] = useLazyQuery<getWeatheringOutput>(GET_WEATHERING);
  const [getAllAlterations] = useLazyQuery<getWeatheringOutput>(GET_WEATHERING, {
    onCompleted: (data) => setAlterationData(data.weathering.items)
  });

  useEffect(() => {
    getWeatherings();
    getAllAlterations();
  }, []);

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(globalPageSizeOptions[0]);

  const [openEditor, setOpenEditor] = useState(false);
  const [savingError, setSavingError] = useState<string | undefined>(undefined);

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);

  const lblSearch = capitalizeFirstLetterEveryword(t(TXT_SEARCH_BY_NAME));

  const fetchWeatherings = () => {
    const variables: getLithologiesInput = {
      code: txtSearch,
      take: pageSize,
      skip: (pageNumber - 1) * pageSize
    };
    getWeatherings({
      variables
    });
  };

  const fetch = (resetPage?: boolean) => {
    if (!!resetPage && pageNumber !== 1) {
      setPageNumber(1);
    } else {
      fetchWeatherings();
    }
  };

  useEffect(() => {
    useApolloErrorHandler(isSignedIn, error);
  }, [error]);

  useEffect(() => {
    fetch();
  }, [pageNumber]);

  useEffect(() => {
    fetch(true);
  }, [pageSize, txtSearch]);

  const openEditWeatheringEditor = (weathering: weathering) => {
    setSelectedWeathering(weathering);
    setOpenEditor(true);
  };

  const openDeleteWeatheringConfirmation = (weathering: weathering) => {
    setSelectedWeathering(weathering);
    setOpenDeleteConfirmation(true);
  };

  const openAddWeatheringEditor = () => {
    setSelectedWeathering(undefined);
    setOpenEditor(true);
  };

  const closeEditor = () => {
    setOpenEditor(false);
  };

  const afterAddingOrUpdatingWeathering = () => {
    setOpenEditor(false);
    if (refetch) {
      refetch();
    }
  };

  const handleAddingOrUpdatingWeatheringError = (error: ApolloError) => {
    if (error.graphQLErrors.length > 0 &&
      !!error.graphQLErrors[0].extensions) {
      setSavingError(error.graphQLErrors[0].extensions['message']);
    } else {
      setSavingError(error.message);
    }
  };

  const [addWeathering, addWeatheringOperation] = useMutation<weathering>(ADD_WEATHERING, {
    onError: (e) => handleAddingOrUpdatingWeatheringError(e),
    onCompleted: (result) => afterAddingOrUpdatingWeathering()
  });

  const [updateWeathering, updateWeatheringOperation] = useMutation<weathering>(UPDATE_WEATHERING, {
    onError: (e) => handleAddingOrUpdatingWeatheringError(e),
    onCompleted: (result) => afterAddingOrUpdatingWeathering()
  });

  const onEditorSave = (payload: addWeatheringInput | updateWeatheringInput) => {
    if (!selectedWeathering) {
      addWeathering({
        variables: {
          input: payload
        }
      });
    } else {
      updateWeathering({
        variables: {
          input: payload
        }
      });
    }
  };

  const onDeleteCancel = () => setOpenDeleteConfirmation(false);

  const [deleteWeathering, deleteWeatheringOperation] = useMutation<boolean>(DELETE_WEATHERING, {
    onError: (e) => { },
    onCompleted: (result) => {
      if (data && refetch) {
        if (data.weathering.items.length == 1) {
          const targetPage = pageNumber - 1 <= 0 ? 1 : pageNumber - 1;
          if (pageNumber !== targetPage) {
            setPageNumber(targetPage);
          } else {
            refetch();
          }
        } else {
          refetch();
        }
      }
      onDeleteCancel();
    }
  });

  const onDelete = () => {
    const input: deleteWeatheringInput = {
      id: selectedWeathering!.id!
    };
    deleteWeathering({
      variables: {
        input
      }
    });
  };

  const onPageSizeChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPageSize(+event.target.value);
  };

  return (
    <div className="content-wrapper">
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>{t(capitalizeFirstLetterEveryword(TXT_WEATHERING))}</h1>
            </div>
          </div>
        </div>
      </section>
      <section className="content">
        <div className="container-fluid">
          {
            openEditor &&
            <ManageWeatheringEditor
              weathering={selectedWeathering}
              onClose={closeEditor}
              onSave={onEditorSave}
              isSaving={
                !selectedWeathering ? addWeatheringOperation.loading :
                  updateWeatheringOperation.loading
              }
              savingError={savingError}
            />
          }
          {
            openDeleteConfirmation &&
            <DeleteConfirmation
              loading={deleteWeatheringOperation.loading}
              onYes={onDelete}
              onNo={onDeleteCancel}
              objectName={selectedWeathering?.label || ''}
            />
          }
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <div className='row'>
                    <div className='col-6'>
                      <div className="form-group">
                        <label>{lblSearch}</label>
                        <input type='text' className='form-control' value={txtSearch} onChange={onTxtSearchChanged} />
                      </div>
                    </div>
                  </div>
                  <div className='row'>
                    <div className='col-md-6'>
                      <button
                        className='btn btn-primary'
                        data-target="#modal-weathering-editor"
                        data-toggle="modal"
                        disabled={loading}
                        onClick={openAddWeatheringEditor}>
                        {t(TXT_ADD_WEATHERING).toUpperCase()}
                      </button>
                    </div>
                    <div className="col-md-6" style={{ textAlign: 'right' }}>
                      <ExportToExcel filename={TXT_WEATHERING} data={alterationData} />
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  <ManageWeatheringsTable
                    loading={loading}
                    data={data}
                    error={error}
                    onEdit={openEditWeatheringEditor}
                    onDelete={openDeleteWeatheringConfirmation}
                  />
                </div>
                <PaginationBar
                  currentPage={pageNumber}
                  totalCount={data?.weathering.totalCount}
                  pageSize={pageSize}
                  onPageChanged={(page) => setPageNumber(page)}
                  onChangeRowsPerPage={onPageSizeChanged}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
